/**
 * TemplateStep Component
 * Step 2A: Browse and select from pre-built templates
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, ChevronRight, ArrowLeft } from 'lucide-react'
import { MacroTemplate, MessageContext } from '../../../types/macros'
import { getTemplatesByMessageContext } from '../../../utils/macroHelpers'

interface TemplateStepProps {
  messageContext: MessageContext | null
  selectedTemplate: string | null
  onSelectTemplate: (templateId: string) => void
  onBack: () => void
  onSkipToCustom: () => void
}

const TemplateStep: React.FC<TemplateStepProps> = ({
  messageContext,
  selectedTemplate,
  onSelectTemplate,
  onBack,
  onSkipToCustom
}) => {
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null)
  
  const templates = getTemplatesByMessageContext(messageContext)

  // Group templates by category
  const groupedTemplates = templates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = []
    }
    acc[template.category].push(template)
    return acc
  }, {} as Record<string, MacroTemplate[]>)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-[#6E6D7A] hover:text-[#1A2B49] mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to context selection
        </button>
        
        <h2 className="text-2xl font-bold text-[#1A2B49] mb-2">
          Choose a Starting Template
        </h2>
        <p className="text-[#6E6D7A]">
          Browse pre-built templates that match your needs. You can customize them in the next step.
        </p>
      </div>

      {/* Template Gallery */}
      <div className="space-y-6">
        {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
          <div key={category}>
            <h3 className="text-lg font-semibold text-[#1A2B49] mb-3 flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-[#FE4215]" />
              {category}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categoryTemplates.map((template) => {
                const isSelected = selectedTemplate === template.id
                const isExpanded = expandedTemplate === template.id

                return (
                  <motion.div
                    key={template.id}
                    layout
                    className={`border-2 rounded-lg transition-all ${
                      isSelected
                        ? 'border-[#FE4215] bg-[#FE4215]/5 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <button
                      onClick={() => onSelectTemplate(template.id)}
                      className="w-full p-4 text-left"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#1A2B49] mb-1">
                            {template.name}
                          </h4>
                          <p className="text-sm text-[#6E6D7A]">
                            {template.description}
                          </p>
                        </div>
                        
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="flex-shrink-0 ml-2 w-6 h-6 bg-[#FE4215] rounded-full flex items-center justify-center"
                          >
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </motion.div>
                        )}
                      </div>

                      {/* Macro Count Badge */}
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          {template.macros.length} {template.macros.length === 1 ? 'macro' : 'macros'}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setExpandedTemplate(isExpanded ? null : template.id)
                          }}
                          className="text-xs text-[#FE4215] hover:underline inline-flex items-center"
                        >
                          {isExpanded ? 'Hide' : 'Show'} details
                          <ChevronRight className={`w-3 h-3 ml-0.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                      </div>
                    </button>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="px-4 pb-4 space-y-2 border-t border-gray-200 pt-3 mt-2 overflow-hidden"
                        >
                          {template.macros.map((macro, index) => (
                            <div key={index} className="p-2 bg-gray-50 rounded text-xs">
                              <div className="font-medium text-gray-700 mb-1">
                                {macro.description || `Macro ${index + 1}`}
                              </div>
                              <div className="font-mono text-gray-600 break-all">
                                {macro.customSyntax || `{{${macro.key}}}`}
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Custom Build Option */}
      <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-[#1A2B49] mb-1">
              Prefer to build from scratch?
            </h3>
            <p className="text-sm text-[#6E6D7A]">
              Skip templates and create a custom macro with our guided builder
            </p>
          </div>
          <button
            onClick={onSkipToCustom}
            className="inline-flex items-center px-4 py-2 bg-white hover:bg-gray-100 text-[#1A2B49] font-medium rounded-lg transition-colors border border-gray-300"
          >
            Build Custom
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default TemplateStep
